import type { Locale } from "../../i18n";

/**
 * Words that belong to the Mission Control v2 surfaces only. Operational codes and
 * actions still come from operator/copy.ts, so one action is described in one place.
 */
const EN = {
  rail: {
    mission: "Mission Control",
    pinned: "Pinned sites",
    monitoring: "Monitoring",
    spend: "Spend this month",
    account: "Account and language",
    more: "Pilots and account",
    toDo: (count: number) => `${count} to do`,
    exit: "Customer studio",
    active: (count: number) => `${count} active`,
  },
  views: { brief: "Brief", inbox: "Inbox", fleet: "Fleet", pipeline: "Pipeline", reports: "Reports", pilots: "Pilots" },
  keys: { brief: "G B", inbox: "G I", fleet: "G F", pipeline: "G P", reports: "G R", pilots: "G A" },
  brief: {
    eyebrow: "Monday brief",
    headline: (shipped: number, decisions: number, improving: number) =>
      decisions === 0 && shipped === 0
        ? "A quiet week. Nothing is waiting for you."
        : `${shipped === 0 ? "No changes shipped yet" : `${shipped} ${shipped === 1 ? "change" : "changes"} shipped`}${improving > 0 ? ` and ${improving} ${improving === 1 ? "site" : "sites"} improved` : ""}. ${decisions === 0 ? "Nothing needs you." : `${decisions} ${decisions === 1 ? "thing needs" : "things need"} you.`}`,
    lead: (attention: number, audits: number, reports: number) =>
      [
        attention > 0 ? `${attention} ${attention === 1 ? "site needs" : "sites need"} attention` : null,
        audits > 0 ? `${audits} fresh ${audits === 1 ? "audit" : "audits"} to read` : null,
        reports > 0 ? `${reports} ${reports === 1 ? "update" : "updates"} drafted` : null,
      ].filter(Boolean).join(", ") || "Everything that runs in the background is running.",
    kpi: {
      shipped: "Changes shipped",
      score: "Avg SEO score",
      waiting: "Waiting on you",
      audits: "Audits completed",
      spend: "Spend this month",
    },
    kpiNote: {
      shipped: "in this period",
      score: (count: number) => `${count} ${count === 1 ? "site assessed" : "sites assessed"}`,
      waiting: "decisions in the inbox",
      audits: "reports to read",
      spend: "month to date",
    },
    decisions: "Needs a decision",
    decisionsHint: "one action clears each one",
    openInbox: "Open inbox",
    moved: "What moved, and why",
    movedHint: "a result linked to the change that caused it",
    movedEmpty: "No shipped or measured change in this period yet.",
    allSites: "All sites",
    stages: "Every website, by stage",
    pipeline: "Pipeline",
    updates: "Client updates, drafted",
    updatesReady: (count: number) => `${count} ${count === 1 ? "ready" : "ready"}`,
    updatesBody: "Each client gets a short email written from their site's week: what shipped, what it did, what is next.",
    noDrafts: "No update is waiting for review.",
    noSites: "No website has been added yet.",
    reviewSend: (count: number) => `Review ${count === 1 ? "the draft" : `all ${count}`}`,
    running: "Running now",
    runningHint: "no action needed",
    runningEmpty: "Nothing is running right now.",
    measuring: (count: number) => `${count} shipped ${count === 1 ? "change" : "changes"} measured in their window.`,
    migration: "Migration",
    work: "Work",
    active: (count: number) => `${count} active`,
  },
  inbox: {
    eyebrow: "Inbox",
    title: (count: number) => (count === 0 ? "Nothing to decide." : `${count} ${count === 1 ? "decision" : "decisions"}.`),
    subtitle: "One list, most urgent first. Each row says the one thing that moves it on.",
    filter: "Filter by site or kind…",
    all: "All",
    alerts: "Alerts",
    setup: "Setup blocked",
    previews: "Previews",
    audits: "Audits",
    updates: "Updates",
    incidents: "Incidents",
    empty: "Nothing here. Background work keeps running.",
    clear: "You are all caught up.",
    select: "Select an item to see what it needs.",
    kind: {
      stopped: "Stopped",
      approve: "Preview",
      audit: "Audit",
      report: "Update",
      setup: "Setup",
      incident: "Incident",
    },
    why: "Why it matters",
    timeline: "What happened",
    checklist: "Onboarding checklist",
    waitingOn: "Waiting on this step",
    expected: "Expected",
    measured: "Measured after shipping",
    noExpected: "No impact estimate was recorded. SiteAgain does not invent one.",
    review: "Review",
    retry: "Try again",
    openSite: "Open site",
    openReport: "Open report",
    take: "Take ownership",
    resolve: "Resolve",
    ownedBy: (who: string) => `Owned by ${who}`,
    changesWaiting: (count: number) => `${count} ${count === 1 ? "change is" : "changes are"} waiting`,
    setupStopped: "The migration stopped; open the site to see why",
    approveChecksPassed: "All checks passed · low risk",
    approveNeedsDecision: "Preview ready · needs your decision",
    severity: { low: "Low", medium: "Medium", high: "High", critical: "Critical" } as Record<string, string>,
    issue: {
      stuck_migration: "Stuck migration",
      url_modification_review: "URL change review",
      indexed_page_deletion: "Indexed page deletion",
      mass_modification: "Mass modification",
      quality_gate_override: "Quality gate override",
      resource_limit_exceeded: "Resource limit exceeded",
      cutover_failure: "Cutover failure",
    } as Record<string, string>,
  },
  fleet: {
    eyebrow: "Fleet",
    title: (sites: number, live: number, blocked: number) =>
      `${sites} ${sites === 1 ? "website" : "websites"}. ${live} live${blocked > 0 ? `, ${blocked} waiting on setup` : ""}.`,
    subtitle: "Every site, its score and the one thing that should happen next.",
    shipped: "Shipped this week",
    waiting: "Waiting on you",
    blocked: "Blocked by setup",
    regressions: "Regressions",
    spend: "Spend MTD",
    all: "All",
    search: "Search site, domain or owner…",
    noMatch: "No websites match this view.",
    columns: {
      website: "Website",
      stage: "Stage",
      health: "Health",
      score: "Score",
      work: "This week",
      waiting: "Waiting",
      setup: "Setup",
      last: "Last signal",
      next: "Next",
    },
    setupLabel: (parts: string[]) => `Setup: ${parts.join(", ")}`,
    setup: { audit: "Audit", preview: "Migrated preview", live: "Live deployment" },
    setupOk: "ready",
    setupMissing: "missing",
    legend: {
      title: "The week at a glance",
      hint: "One square per site per day, from stored events.",
      ship: "Shipped",
      audit: "Audit / preview",
      client: "Client touch",
      alert: "Alert",
    },
    noEvents: "No events recorded in this period for these sites.",
    incidents: (count: number) => `${count} ${count === 1 ? "incident" : "incidents"}`,
  },
  pipeline: {
    eyebrow: "Pipeline",
    title: "From audit finding to measured result.",
    subtitle: "Every recommendation and change in the stage it is really in. Empty stages stay empty.",
    approved: "Approved",
    approveAll: "Approve all eligible",
    eligible: (count: number) => `${count} low-risk ${count === 1 ? "change is" : "changes are"} ready`,
    batchExplain: "Only changes whose every check passed and whose preview you have not changed are included.",
    allChecks: "All checks passed",
    batchConfirm: (count: number) => `Approve ${count} ${count === 1 ? "change" : "changes"}? Each keeps its own preview, evidence and rollback.`,
    more: (count: number) => `+ ${count} more`,
    noCards: "Nothing at this stage.",
  },
  add: {
    eyebrow: "Add website · one form, no wizard",
    title: "Paste a URL. SiteAgain does the rest.",
    step1: "Website",
    step2: "What matters most?",
    step3: "What happens next",
    step4: "How it runs",
    urlHint: "Paste the address. The full audit starts as soon as the website is added.",
    urlInvalid: "That does not look like a website address.",
    nameLabel: "Name",
    nameHint: "Filled in from the address. Change it if you like.",
    ownerLabel: "Owner email (optional)",
    ownerPlaceholder: "owner@example.com",
    languageLabel: "Site language",
    next: ["Reachable check and full audit run on their own.", "The audit lands in your Inbox with a draft email.", "Nothing on the live site changes until you approve a preview."],
    auditName: "First audit",
    auditDesc: "Runs now. About four minutes.",
    mailName: "Weekly email",
    mailDesc: "Written from the site's week, once a client is on a plan.",
    summary: (name: string, goal: string) =>
      `${name || "This website"} is added in ${goal} mode. The full audit runs now and its draft waits in your Inbox.`,
    cta: "Add and run audit",
    cancel: "Cancel",
    close: "Close",
    statusReady: "ready",
    statusWaiting: "waiting for the URL",
    statusInbox: "Inbox",
    statusApproval: "approval gate",
    statusPlan: "On a plan",
  },
  reports: {
    eyebrow: "Reports",
    title: "The month, reviewed before it is sent.",
    subtitle: "A draft is opened and read exactly as the customer will see it.",
  },
  site: {
    breadcrumb: "Fleet",
    week: "This week",
    changes: "Changes",
    audit: "Audit",
    search: "Search & AI",
    setup: "Setup",
    client: "Client",
    reports: "Reports",
    summaryEyebrow: "The week in three lines",
    summary: (shipped: number, waiting: number, score: number | null, incidents: number) =>
      [
        `${shipped} ${shipped === 1 ? "change" : "changes"} went live this period`,
        waiting > 0 ? `${waiting} ${waiting === 1 ? "is" : "are"} waiting for a decision` : "nothing is waiting for a decision",
        score === null ? "No audit has completed yet" : `The latest audit is ${Math.round(score)}/100`,
        incidents > 0 ? `${incidents} open ${incidents === 1 ? "incident needs" : "incidents need"} a person` : null,
      ].filter(Boolean).join(". ") + ".",
    scores: { score: "SEO score", shipped: "Shipped", waiting: "Waiting", recs: "Recommendations", enquiries: "Enquiries" },
    dayByDay: "Day by day",
    dayHint: "every event comes from a stored record; open its change, audit or report",
    changePipeline: "Change pipeline",
    changeHint: "from audit finding to measured result",
    latestAudit: "Latest audit",
    setupTitle: "Setup",
    setupComplete: "Complete",
    setupPartial: "In progress",
    searchTitle: "Search Console",
    clientTitle: "Client",
    weeklyEmail: "Weekly email",
    weeklyEmailValue: "Draft in Reports",
    approved: "Last approved change",
    enquiriesValue: "last 28 days",
    openClient: "Open client records",
    notConnected: "Not connected",
    noChanges: "No changes yet.",
    noRecs: "No recommendations yet.",
    guardrails: "Guardrails",
    guardrailsBody: "Guardrails are enforced by the change workflow: nothing publishes without the SEO contract and the approval gate.",
    last7: "last 7 days",
    toDecide: "to decide",
    waitingNote: "Preview",
    needsDecision: "Needs your decision",
    live: "Live",
    eventShipped: "Shipped",
    eventRolledBack: "Rolled back",
    eventAudit: "Audit",
    eventReport: "Report drafted",
    eventPreview: "Preview",
  },
};

const FR: typeof EN = {
  rail: {
    mission: "Mission Control",
    pinned: "Sites épinglés",
    monitoring: "Surveillance",
    spend: "Dépenses ce mois",
    account: "Compte et langue",
    more: "Pilotes et compte",
    toDo: (count) => `${count} à faire`,
    exit: "Espace client",
    active: (count) => `${count} ${count === 1 ? "actif" : "actifs"}`,
  },
  views: { brief: "Brief", inbox: "Boîte", fleet: "Parc", pipeline: "Flux", reports: "Rapports", pilots: "Pilotes" },
  keys: { brief: "G B", inbox: "G I", fleet: "G F", pipeline: "G P", reports: "G R", pilots: "G A" },
  brief: {
    eyebrow: "Brief du lundi",
    headline: (shipped, decisions, improving) =>
      decisions === 0 && shipped === 0
        ? "Une semaine calme. Rien ne vous attend."
        : `${shipped === 0 ? "Aucun changement publié" : `${shipped} ${shipped === 1 ? "changement publié" : "changements publiés"}`}${improving > 0 ? ` et ${improving} ${improving === 1 ? "site progresse" : "sites progressent"}` : ""}. ${decisions === 0 ? "Rien ne vous attend." : `${decisions} ${decisions === 1 ? "élément vous attend" : "éléments vous attendent"}.`}`,
    lead: (attention, audits, reports) =>
      [
        attention > 0 ? `${attention} ${attention === 1 ? "site demande" : "sites demandent"} attention` : null,
        audits > 0 ? `${audits} ${audits === 1 ? "audit frais" : "audits frais"} à lire` : null,
        reports > 0 ? `${reports} ${reports === 1 ? "mise à jour rédigée" : "mises à jour rédigées"}` : null,
      ].filter(Boolean).join(", ") || "Tout ce qui tourne en arrière-plan tourne.",
    kpi: {
      shipped: "Changements publiés",
      score: "Score SEO moyen",
      waiting: "En attente de vous",
      audits: "Audits terminés",
      spend: "Dépenses ce mois",
    },
    kpiNote: {
      shipped: "sur la période",
      score: (count) => `${count} ${count === 1 ? "site évalué" : "sites évalués"}`,
      waiting: "décisions dans la boîte",
      audits: "rapports à lire",
      spend: "mois en cours",
    },
    decisions: "Une décision s'impose",
    decisionsHint: "une action suffit pour chacun",
    openInbox: "Ouvrir la boîte",
    moved: "Ce qui a bougé, et pourquoi",
    movedHint: "un résultat relié au changement qui l'a causé",
    movedEmpty: "Aucun changement publié ou mesuré sur cette période.",
    allSites: "Tous les sites",
    stages: "Chaque site, par étape",
    pipeline: "Flux",
    updates: "Mises à jour clients, rédigées",
    updatesReady: (count) => `${count} ${count === 1 ? "prête" : "prêtes"}`,
    updatesBody: "Chaque client reçoit un court e-mail écrit depuis la semaine de son site : ce qui a été publié, ce que cela a fait, la suite.",
    noDrafts: "Aucune mise à jour en attente de relecture.",
    noSites: "Aucun site n'a encore été ajouté.",
    reviewSend: (count) => `Relire ${count === 1 ? "le brouillon" : `les ${count}`}`,
    running: "En cours",
    runningHint: "aucune action nécessaire",
    runningEmpty: "Rien ne tourne en ce moment.",
    measuring: (count) => `${count} ${count === 1 ? "changement publié mesuré" : "changements publiés mesurés"} sur leur fenêtre.`,
    migration: "Migration",
    work: "Traitement",
    active: (count) => `${count} en cours`,
  },
  inbox: {
    eyebrow: "Boîte",
    title: (count) => (count === 0 ? "Rien à décider." : `${count} ${count === 1 ? "décision" : "décisions"}.`),
    subtitle: "Une liste, du plus urgent au moins urgent. Chaque ligne dit la seule action qui la fait avancer.",
    filter: "Filtrer par site ou type…",
    all: "Tout",
    alerts: "Alertes",
    setup: "Bloqués à la configuration",
    previews: "Aperçus",
    audits: "Audits",
    updates: "Mises à jour",
    incidents: "Incidents",
    empty: "Rien ici. Le travail de fond continue.",
    clear: "Vous êtes à jour.",
    select: "Choisissez un élément pour voir ce qu'il demande.",
    kind: { stopped: "Arrêté", approve: "Aperçu", audit: "Audit", report: "Mise à jour", setup: "Config", incident: "Incident" },
    why: "Pourquoi c'est important",
    timeline: "Ce qui s'est passé",
    checklist: "Étapes de configuration",
    waitingOn: "En attente de cette étape",
    expected: "Attendu",
    measured: "Mesuré après publication",
    noExpected: "Aucune estimation n'a été enregistrée. SiteAgain n'en invente pas.",
    review: "Relire",
    retry: "Réessayer",
    openSite: "Ouvrir le site",
    openReport: "Ouvrir le rapport",
    take: "Prendre en charge",
    resolve: "Résoudre",
    ownedBy: (who) => `Pris en charge par ${who}`,
    changesWaiting: (count) => `${count} ${count === 1 ? "changement attend" : "changements attendent"}`,
    setupStopped: "La migration s'est arrêtée ; ouvrez le site pour voir pourquoi",
    approveChecksPassed: "Toutes les vérifications passées · faible risque",
    approveNeedsDecision: "Aperçu prêt · attend votre décision",
    severity: { low: "Faible", medium: "Moyenne", high: "Haute", critical: "Critique" } as Record<string, string>,
    issue: {
      stuck_migration: "Migration bloquée",
      url_modification_review: "Revue de changement d'URL",
      indexed_page_deletion: "Suppression de page indexée",
      mass_modification: "Modification de masse",
      quality_gate_override: "Dérogation au contrôle qualité",
      resource_limit_exceeded: "Limite de ressources dépassée",
      cutover_failure: "Échec de bascule",
    } as Record<string, string>,
  },
  fleet: {
    eyebrow: "Parc",
    title: (sites, live, blocked) =>
      `${sites} ${sites === 1 ? "site" : "sites"}. ${live} en ligne${blocked > 0 ? `, ${blocked} en attente de configuration` : ""}.`,
    subtitle: "Chaque site, son score et la prochaine chose à faire.",
    shipped: "Publiés cette semaine",
    waiting: "En attente de vous",
    blocked: "Bloqués par la config",
    regressions: "Régressions",
    spend: "Dépenses du mois",
    all: "Tous",
    search: "Rechercher un site, un domaine, un propriétaire…",
    noMatch: "Aucun site ne correspond.",
    columns: {
      website: "Site",
      stage: "Étape",
      health: "Santé",
      score: "Score",
      work: "Cette semaine",
      waiting: "En attente",
      setup: "Config",
      last: "Dernier signal",
      next: "Ensuite",
    },
    setupLabel: (parts) => `Configuration : ${parts.join(", ")}`,
    setup: { audit: "Audit", preview: "Aperçu migré", live: "Mise en ligne" },
    setupOk: "prêt",
    setupMissing: "manquant",
    legend: {
      title: "La semaine en un regard",
      hint: "Une case par site et par jour, depuis les événements enregistrés.",
      ship: "Publié",
      audit: "Audit / aperçu",
      client: "Contact client",
      alert: "Alerte",
    },
    noEvents: "Aucun événement enregistré sur la période pour ces sites.",
    incidents: (count) => `${count} ${count === 1 ? "incident" : "incidents"}`,
  },
  pipeline: {
    eyebrow: "Flux",
    title: "De la découverte de l'audit au résultat mesuré.",
    subtitle: "Chaque recommandation et changement dans son étape réelle. Une étape vide reste vide.",
    approved: "Approuvé",
    approveAll: "Tout approuver",
    eligible: (count) => `${count} ${count === 1 ? "changement à faible risque est prêt" : "changements à faible risque sont prêts"}`,
    batchExplain: "Seuls les changements dont toutes les vérifications sont passées et dont l'aperçu n'a pas changé sont inclus.",
    allChecks: "Toutes les vérifications passées",
    batchConfirm: (count) => `Approuver ${count} ${count === 1 ? "changement" : "changements"} ? Chacun garde son aperçu, ses preuves et son retour arrière.`,
    more: (count) => `+ ${count} de plus`,
    noCards: "Rien à cette étape.",
  },
  add: {
    eyebrow: "Ajouter un site · un seul formulaire",
    title: "Collez une URL. SiteAgain fait le reste.",
    step1: "Site",
    step2: "Qu'est-ce qui compte le plus ?",
    step3: "Ce qui se passe ensuite",
    step4: "Comment ça tourne",
    urlHint: "Collez l'adresse. L'audit complet démarre dès que le site est ajouté.",
    urlInvalid: "Cela ne ressemble pas à une adresse de site.",
    nameLabel: "Nom",
    nameHint: "Rempli depuis l'adresse. Modifiez-le si vous voulez.",
    ownerLabel: "E-mail du propriétaire (facultatif)",
    ownerPlaceholder: "proprietaire@exemple.fr",
    languageLabel: "Langue du site",
    next: ["La vérification d'accès et l'audit complet tournent seuls.", "L'audit arrive dans votre boîte avec un e-mail rédigé.", "Rien ne change sur le site en ligne avant votre approbation."],
    auditName: "Premier audit",
    auditDesc: "Lance maintenant. Environ quatre minutes.",
    mailName: "E-mail hebdomadaire",
    mailDesc: "Écrit depuis la semaine du site, une fois le client sur un forfait.",
    summary: (name, goal) =>
      `${name || "Ce site"} est ajouté en mode ${goal}. L'audit complet tourne maintenant et son brouillon attend dans votre boîte.`,
    cta: "Ajouter et lancer l'audit",
    cancel: "Annuler",
    close: "Fermer",
    statusReady: "prêt",
    statusWaiting: "en attente de l'URL",
    statusInbox: "Boîte",
    statusApproval: "validation",
    statusPlan: "Sur un forfait",
  },
  reports: {
    eyebrow: "Rapports",
    title: "Le mois, relu avant d'être envoyé.",
    subtitle: "Un brouillon s'ouvre et se lit exactement comme le client le verra.",
  },
  site: {
    breadcrumb: "Parc",
    week: "Cette semaine",
    changes: "Changements",
    audit: "Audit",
    search: "Recherche & IA",
    setup: "Config",
    client: "Client",
    reports: "Rapports",
    summaryEyebrow: "La semaine en trois lignes",
    summary: (shipped, waiting, score, incidents) =>
      [
        `${shipped} ${shipped === 1 ? "changement publié" : "changements publiés"} sur la période`,
        waiting > 0 ? `${waiting} ${waiting === 1 ? "attend" : "attendent"} une décision` : "rien n'attend de décision",
        score === null ? "Aucun audit terminé" : `Le dernier audit est à ${Math.round(score)}/100`,
        incidents > 0 ? `${incidents} ${incidents === 1 ? "incident ouvert demande" : "incidents ouverts demandent"} une personne` : null,
      ].filter(Boolean).join(". ") + ".",
    scores: { score: "Score SEO", shipped: "Publiés", waiting: "En attente", recs: "Recommandations", enquiries: "Demandes" },
    dayByDay: "Jour par jour",
    dayHint: "chaque événement vient d'un enregistrement ; ouvrez son changement, audit ou rapport",
    changePipeline: "Flux des changements",
    changeHint: "de la découverte à la mesure",
    latestAudit: "Dernier audit",
    setupTitle: "Configuration",
    setupComplete: "Complet",
    setupPartial: "En cours",
    searchTitle: "Search Console",
    clientTitle: "Client",
    weeklyEmail: "E-mail hebdomadaire",
    weeklyEmailValue: "Brouillon dans Rapports",
    approved: "Dernier changement approuvé",
    enquiriesValue: "28 derniers jours",
    openClient: "Ouvrir les données client",
    notConnected: "Non connecté",
    noChanges: "Aucun changement.",
    noRecs: "Aucune recommandation.",
    guardrails: "Garde-fous",
    guardrailsBody: "Les garde-fous sont appliqués par le flux de changement : rien ne publie sans le contrat SEO et l'approbation.",
    last7: "7 derniers jours",
    toDecide: "à décider",
    waitingNote: "Aperçu",
    needsDecision: "Attend votre décision",
    live: "En ligne",
    eventShipped: "Publié",
    eventRolledBack: "Annulé",
    eventAudit: "Audit",
    eventReport: "Rapport rédigé",
    eventPreview: "Aperçu",
  },
};

export type MC2Copy = typeof EN;

export function mc2Copy(locale: Locale): MC2Copy {
  return locale === "fr" ? FR : EN;
}
